/* eslint-disable camelcase */
import {
  GTMEcommerceItem,
  GTMItemListName,
  GtmProductType,
  GTMProductTypes,
  GTMViewContentGroup,
  ItemSize
} from '@lib/models/gtm'
import { ProductLiteCategorized, Product, ProductSize, ProductType } from '@lib/models/product'
import { CartProduct } from '@lib/models/purchase'
import { useAppStore } from '@stores/app.store'
import get from 'lodash/get'
import { isPlaywright } from './testUtils'
import { WithdrawalMethod } from '@lib/models/order'
import { Filters, PRODUCT_CATEGORY_FILTER_PREFIX } from '@lib/models/internal/filters'
import { ImUserTag } from '@lib/models/identification'

export const createGTMScript = (containerId: string) => {
  // special behaviour for tests
  const gtmContainerID = isPlaywright() ? 'GTM-XXXYYYY' : containerId
  return `
  (function(w,d,s,l,i){
    w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});
    var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';
    j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
    f.parentNode.insertBefore(j,f);
  })(window,document,'script','dataLayer','${gtmContainerID}');`
}

export const createGTMNoScriptSrc = (containerId: string) => {
  return `https://www.googletagmanager.com/ns.html?id=${containerId}`
}

export const generatePageTitle = (content: GTMViewContentGroup) => {
  if (content.pageTitle) return content.pageTitle
  const arr = [content.cg, content.cg2, content.cg3].filter(Boolean)
  return arr.join('/')
}

export const getCustomHashLocation = () => {
  const host = 'https://adk.com'
  if (!globalThis) return `${host}/`
  const hashPath = globalThis.location.hash.replace('#', '') || '/'
  return `${host}${hashPath}`
}

export const getItemListNameTypeFromProducts = (
  products: GTMEcommerceItem[]
): GtmProductType | undefined => {
  let crossSellingCMS = false
  let crossSellingNBO = false
  let category
  for (const product of products) {
    if (product.item_list_name === GTMProductTypes.CROSS_SELLING_CMS) crossSellingCMS = true
    if (product.item_list_name === GTMProductTypes.CROSS_SELLING_NBO) crossSellingNBO = true
    if (crossSellingCMS && crossSellingNBO) return GTMProductTypes.CROSS_SELLING_MIX
  }
  if (crossSellingCMS) return GTMProductTypes.CROSS_SELLING_CMS
  if (crossSellingNBO) return GTMProductTypes.CROSS_SELLING_NBO
  return category
}

export const formatAmountGTM = (amount: number) => (amount ? amount / 100 : 0)

export const formatItemSize = (size: string | undefined) => {
  if (size === 'NONE' || !size) return ItemSize.UNIQUE
  return ItemSize[size]
}

const calculatedItemListName = (productType: string): string =>
  productType === ProductType.CROSSSELLING
    ? GTMProductTypes.CROSS_SELLING_CMS
    : GTMProductTypes.CROSS_SELLING_NBO

const productPriceAmountGTM = (product: ProductLiteCategorized | Product) =>
  product.unifiedPrice?.amount
    ? formatAmountGTM(product.unifiedPrice.amount)
    : formatAmountGTM(product.price.amount)

export const gtmEcommerceItemFromLiteCategorized = ({
  product,
  itemListName,
  currency,
  computedUnitPrice,
  available,
  isLoyalty = false,
  aopCoupon
}: {
  product: ProductLiteCategorized
  itemListName?: string
  currency: string
  computedUnitPrice?: number
  available?: boolean
  isLoyalty?: boolean
  aopCoupon?: string
}): GTMEcommerceItem => {
  if (!itemListName) {
    itemListName = product.productType
      ? calculatedItemListName(product.productType)
      : GTMItemListName.OTHER
  }
  const price = computedUnitPrice
    ? formatAmountGTM(computedUnitPrice)
    : productPriceAmountGTM(product)

  return {
    ...new GTMEcommerceItem(),
    currency,
    item_name: product.name,
    item_id: product.identifier,
    item_list_name: itemListName,
    item_variant: formatItemSize(product.size),
    price: isLoyalty ? 0 : price,
    quantity: 1,
    is_combo: product.combo,
    loyalty: isLoyalty,
    points: isLoyalty ? (product.points ?? 0) : 0,
    item_available: available,
    item_category: gtmEcommerceItemGetCategory(product.idCategory),
    coupons: aopCoupon
  }
}

export const gtmEcommerceItemFromSize = ({
  product,
  itemListName,
  currency,
  available
}: {
  product: ProductSize
  itemListName?: string
  currency: string
  available?: boolean
}): GTMEcommerceItem => {
  return {
    ...new GTMEcommerceItem(),
    currency,
    item_name: product.name,
    item_id: product.code,
    item_list_name: itemListName ?? GTMItemListName.OTHER,
    item_variant: formatItemSize(product.id),
    price: formatAmountGTM(product.price.amount),
    quantity: 1,
    is_combo: product.combo,
    loyalty: false,
    points: 0,
    item_available: available,
    item_category: gtmEcommerceItemGetCategory(product.idCategory)
  }
}

export const gtmEcommerceItemFromProduct = ({
  product,
  itemListName,
  currency,
  isLoyalty = false,
  aopCoupon
}: {
  product: Product
  itemListName?: string
  currency: string
  isLoyalty?: boolean
  aopCoupon?: string
}): GTMEcommerceItem => {
  const price = productPriceAmountGTM(product)

  if (!itemListName) {
    itemListName = product.productType
      ? calculatedItemListName(product.productType)
      : GTMItemListName.OTHER
  }
  return {
    ...new GTMEcommerceItem(),
    currency,
    item_name: product.name,
    item_id: product.identifier,
    item_list_name: itemListName,
    is_combo: product.combo,
    item_variant: formatItemSize(product.size),
    price: isLoyalty ? 0 : price,
    quantity: 1,
    loyalty: isLoyalty,
    points: isLoyalty ? (product.points ?? 0) : 0,
    item_category: gtmEcommerceItemGetCategory(product.idCategory),
    coupons: aopCoupon
  }
}

export const gtmSanitizeString = (str: string) => str.trim().toLowerCase().replaceAll(' ', '_')

export const getCategoryItemListName = ({ isFeatured, categoryTitle }: { isFeatured: boolean, categoryTitle: string }) => {
  const categoryTitleSanitized = gtmSanitizeString(categoryTitle)
  return isFeatured ? `home_${categoryTitleSanitized}` : `category_${categoryTitleSanitized}`
}

export const withItemIndexes = (items: GTMEcommerceItem[]): GTMEcommerceItem[] =>
  items.map((item, i) => ({ ...item, index: i + 1 }))

// 1-based position of `identifier` within `items` (the *unfiltered* list — Category.vue and
// Loyalty.vue both call this with their unfiltered product array, never the on-screen filtered
// one, per the view_item_list/select_item index spec). Returns undefined rather than 0 when the
// product isn't found, so a broken filteredProducts-is-a-subset-of-unfiltered invariant fails
// loudly as "no index" instead of silently sending a fake-looking index: 0 to GTM.
export const getUnfilteredItemIndex = (
  items: Array<{ identifier: string }>,
  identifier: string
): number | undefined => {
  const index = items.findIndex((item) => item.identifier === identifier)
  return index === -1 ? undefined : index + 1
}

export const gtmEcommerceItemGetCategory = (productIdCategory: string) => {
  const appStore = useAppStore()

  const productCategory = appStore.categories.find(
    (category) => category.idCategory === productIdCategory
  )

  return productCategory ? productCategory.title.toLowerCase() : ''
}

export const getAopCouponAppliedForCartProduct = ({
  cartProduct,
  aopCouponsApplied
}: {
  cartProduct: CartProduct
  aopCouponsApplied?: string[]
}) => {
  if (!cartProduct.barcode) return undefined
  if (!aopCouponsApplied?.length) return undefined

  const barcode = cartProduct.barcode
  return aopCouponsApplied.find((aopCoupon) => aopCoupon.startsWith(barcode))
}

export const gtmEcommerceItemFromCartProduct = ({
  cartProduct,
  currency,
  aopCouponsApplied
}: {
  cartProduct: CartProduct
  currency: string
  aopCouponsApplied?: string[]
}): GTMEcommerceItem => {
  const price = formatAmountGTM(cartProduct.priceWithExtras)
  const isLoyalty = !!cartProduct.points

  // Prefer the value persisted by the backend since this line was added to the cart — it's
  // the original item_list_name the front resolved at add-to-cart time, and it must not be
  // recomputed on every later cart-derived event (view_cart, begin_checkout, purchase, ...).
  let itemListName = cartProduct.itemListName

  if (!itemListName) {
    switch (true) {
      case isLoyalty:
        itemListName = GTMItemListName.LOYALTY_HOME
        break
      case !!cartProduct.productType:
        itemListName = calculatedItemListName(cartProduct.productType)
        break
      default:
        itemListName = GTMItemListName.OTHER
    }
  }

  const aopCouponApplied = getAopCouponAppliedForCartProduct({
    cartProduct,
    aopCouponsApplied
  })

  return {
    ...new GTMEcommerceItem(),
    currency,
    item_list_name: itemListName,
    item_name: cartProduct.name,
    item_id: cartProduct.identifier,
    price: isLoyalty ? 0 : price,
    quantity: cartProduct.units,
    item_category: gtmEcommerceItemGetCategory(cartProduct.idCategory),
    is_combo: cartProduct.combo,
    loyalty: isLoyalty,
    points: isLoyalty ? (cartProduct.points ?? 0) : 0,
    item_variant: formatItemSize(cartProduct.size),
    coupons: aopCouponApplied
  }
}

export const extractCustomError = ({
  error,
  customHttpStatus = undefined
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any
  customHttpStatus?: number
}) => {
  // defaults
  const defaultStatusCode = 500
  const defaultMessage = 'Internal Server Error'

  const errResponse = error?.response || undefined

  // status
  const statusCode = get(errResponse, 'status')

  if (!errResponse || !statusCode)
    return { code: `${defaultStatusCode}`, type: error?.name || defaultMessage }

  // messages
  const message = get(errResponse, 'data.message')
  // IM message
  const errors = get(errResponse, 'data.error', {})
  const IMErrorMessageKey = Object.keys(errors).length
    ? errors.message
    : get(errResponse, 'data.code', '')
  const stringifiedResponse = JSON.stringify(get(errResponse, 'data', defaultMessage))

  const code = customHttpStatus ?? statusCode ?? defaultStatusCode
  const type = IMErrorMessageKey || message || stringifiedResponse

  return {
    code: `${code}`,
    type
  }
}

export const buildGtmFilterAll = (filterKey: 'tier' | 'name') => {
  // name_all, points_all
  return `${filterKey.replace('tier', 'points')}_all`
}

export const buildGtmFilters = (filters?: string[]) => {
  if (!filters?.length || (filters.length === 1 && filters[0] === 'all')) return undefined
  const gtmFilters = filters.map((filter) => {
    if (filter.startsWith(`${Filters.NAME}:`)) {
      // name:CHF_PRODUCT_CATEGORY_CHICKEN => name_chicken
      filter = filter.replace(PRODUCT_CATEGORY_FILTER_PREFIX, '')
    } else if (filter.startsWith(`${Filters.TIER}:`)) {
      // tier:120 => points_120
      filter = filter.replace('tier', 'points')
    }
    filter = filter.replace(':', '_').toLowerCase()
    return filter
  })

  return gtmFilters.join(';')
}

export const getGtmPickupType = (type: string) => {
  if (type === WithdrawalMethod.AT_TABLE) return 'table'
  else if (type === WithdrawalMethod.AT_COUNTER) return 'counter'
  return undefined
}

export const getGTMUserTypeFromSegments = (segments: Array<string>) => {
  if (!segments?.length) return undefined
  return segments[0].trim().replaceAll(' ', '_').toLowerCase()
}

export const getGTMUserIsEmployee = (tags: Array<ImUserTag>) => {
  if (!tags?.length) return false
  return tags.some((tag) => tag.value.startsWith('mcd_employee'))
}

export const calculateGtmValueFromGtmItems = (items: GTMEcommerceItem[]) => {
  if (!items.length) return 0
  return items.reduce((acc, item) => acc + (item.price || 0) * (item.quantity || 1), 0)
}
